import type { DocumentData, DocumentReference } from 'firebase/firestore'
import {
  CollectionReference,
  collection,
  doc,
  orderBy,
  query,
  serverTimestamp,
  writeBatch,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { FetchedCollection } from '../../firestore-mobx'
import {
  convertDocumentSnapshotToModel,
  modelListStream,
} from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { SlideDeckRole } from '../../models/SlideDeckRole'
import type {
  FirestoreSlideDeckRole,
  FirestoreSlideDeckRoleWrite,
} from './schema'
import { schema } from './schema'
import {
  addDocWithError,
  deleteDocWithError,
  getDocsWithError,
  updateDocWithError,
} from '../../firestore-mobx/fetch'
import type { RoleFields } from './types'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreSlideDeckRole> = {
  toFirestore: (data) => data,
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

const getColRef = (
  firestore: Firestore,
  slideDeckId: string
): CollectionReference<FirestoreSlideDeckRole, DocumentData> => {
  return collection(firestore, 'slide_deck', slideDeckId, 'role').withConverter(
    converter
  )
}

const getDocRef = (
  firestore: Firestore,
  {
    slideDeckId,
    slideDeckRoleId,
  }: { slideDeckId: string; slideDeckRoleId: string }
): DocumentReference<FirestoreSlideDeckRole, DocumentData> => {
  return doc(getColRef(firestore, slideDeckId), slideDeckRoleId)
}

export const buildFirestoreSlideDeckRolesFetchedCollection = (
  firestore: Firestore,
  slideDeckId: string
): FetchedCollection<FirestoreSlideDeckRole> => {
  const ref = getColRef(firestore, slideDeckId)

  return new FetchedCollection<FirestoreSlideDeckRole>(ref, (collectionRef) =>
    query(collectionRef, orderBy('order', 'asc'))
  )
}

export const fetchSlideDeckRoles = async (
  repository: FirebaseRepository,
  params: { slideDeckId: string }
) => {
  const ref = getColRef(repository.firestore, params.slideDeckId)
  const q = query(ref, orderBy('order'))
  const snapshot = await getDocsWithError(q, 'FetchSlideDeckRolesError')
  return snapshot.docs.map((snapshotDoc) => {
    return convertDocumentSnapshotToModel(
      repository,
      snapshotDoc,
      SlideDeckRole
    )
  })
}

export const getSlideDeckRoles = (
  repository: FirebaseRepository,
  params: {
    slideDeckId: string
  }
) => {
  const ref = getColRef(repository.firestore, params.slideDeckId)
  const orderedQuery = query(ref, orderBy('order', 'asc'))
  return modelListStream(repository, orderedQuery, SlideDeckRole)
}

export const saveSlideDeckRole = async (
  repository: FirebaseRepository,
  {
    roleFields,
    slideDeckId,
    roleId,
  }: {
    slideDeckId: string
    roleFields: RoleFields
    roleId?: string
  }
) => {
  type WriteKey = keyof FirestoreSlideDeckRoleWrite

  const dataToWrite: Partial<Record<WriteKey, unknown>> = {
    ...roleFields,
    updatedAt: serverTimestamp(),
  }

  if (!roleId) dataToWrite.order = 999

  const ref = roleId
    ? doc(getColRef(repository.firestore, slideDeckId), roleId)
    : getColRef(repository.firestore, slideDeckId)

  if (ref instanceof CollectionReference) {
    const newRole = await addDocWithError(
      ref,
      dataToWrite,
      'CreateSlideDeckRoleError'
    )
    // Reorder roles after creating a new one
    await reorderSlideDeckRoles(repository, slideDeckId)
    return newRole
  }

  return await updateDocWithError(ref, dataToWrite, 'UpdateSlideDeckRoleError')
}

const reorderSlideDeckRoles = async (
  repository: FirebaseRepository,
  slideDeckId: string
) => {
  // Get all current roles
  const roles = await fetchSlideDeckRoles(repository, { slideDeckId })
  const roleIds = roles.map((role) => role.id)

  // Call sortSlideDeckRoles to set proper order indices
  await sortSlideDeckRoles(repository, {
    slideDeckId,
    currentOrder: roleIds,
  })
}

export const deleteSlideDeckRole = async (
  repository: FirebaseRepository,
  { roleId, slideDeckId }: { roleId: string; slideDeckId: string }
) => {
  await deleteDocWithError(
    getDocRef(repository.firestore, { slideDeckId, slideDeckRoleId: roleId }),
    'DeleteSlideDeckRoleError'
  )
}

/**
 * Sort the roles in the slide_deck by the order field and update
 * the order field with the index of the role in the list so that
 * the order is always correct and the roles are always sorted by
 * the order field when the slide_deck is loaded from Firestore.
 *
 * if **oldIndex** and **newIndex** are supplied then move element at **oldIndex** to **newIndex**
 */
export const sortSlideDeckRoles = async (
  repository: FirebaseRepository,
  {
    currentOrder,
    oldIndex,
    newIndex,
    slideDeckId,
  }: {
    slideDeckId: string
    currentOrder: string[]
    oldIndex?: number
    newIndex?: number
  }
) => {
  const roleIds = [...currentOrder]

  // if old index and new index supplied then reorder
  if (oldIndex !== undefined && newIndex !== undefined) {
    //remove at old index
    const targetId = roleIds.splice(oldIndex, 1)[0]

    //insert at new index
    roleIds.splice(newIndex, 0, targetId)
  }

  const batch = writeBatch(repository.firestore)
  roleIds.forEach((roleId, index) => {
    // get the role doc ref
    const roleRef = doc(getColRef(repository.firestore, slideDeckId), roleId)
    batch.update(roleRef, { order: index })
  })

  // perform the batch write
  await batch.commit()
}
